import { FeatureName } from '@features';
import { FeatureStorageCookie, FeatureStorageServer } from './feature-storage';

export const cookieName = 'feature_state';
const cookieStorage = new FeatureStorageCookie(cookieName);

class ToggleState {
    private _cookieState: Map<string, boolean>;
    private serverState: Map<string, boolean> = new Map();

    public constructor () {
        this._cookieState = cookieStorage.read();
    }

    public setServerState (state: FeatureStorageServer): void {
        this.serverState = state.load();
    }

    public getServerKeys (): string[] {
        return [...this.serverState.keys()];
    }

    public getServer (name: string): boolean | null {
        return this.serverState.get(name) ?? null;
    }

    public getLocal (name: string): boolean | null {
        return this._cookieState.get(name) ?? null;
    }

    public get (name: string): boolean {
        return this._cookieState.get(name) ?? this.serverState.get(name) ?? false;
    }

    public set (name: string, state: boolean): void {
        this._cookieState.set(name, state);
        cookieStorage.write(this._cookieState);
    }

    public reset (): void {
        this._cookieState.clear();
        cookieStorage.write(this._cookieState);
    }
}

let _state: ToggleState | undefined;

const state: () => ToggleState = () => {
    if (_state === undefined) {
        _state = new ToggleState();
    }

    return _state;
};

class FeatureToggle {
    constructor (readonly name: FeatureName) {
    }

    get enabled (): boolean {
        return state().get(this.name.toString());
    }

    set enabled (_state: boolean) {
        state().set(this.name.toString(), _state);
    }
}

/**
 * Helper function to create a toggle w/o calling new
 */
export function Feature (name: FeatureName) {
    return new FeatureToggle(name);
}

export default {
    get (name: FeatureName): FeatureToggle {
        return new FeatureToggle(name);
    },

    getAll (): FeatureToggle[] {
        return this.getNames().map((key: string | number) => new FeatureToggle(key as FeatureName));
    },

    getNames (): string[] {
        return state().getServerKeys();
    },

    enabled (name: FeatureName): boolean {
        return this.get(name).enabled;
    },

    /**
     * @param serverState JSON-serialized object of feature toggle name => state (boolean)
     */
    setServerState (serverState: string): void {
        state().setServerState(new FeatureStorageServer(serverState));
    },

    getToggleState (): ToggleState {
        return state();
    },

    resetLocalToggleState (): void {
        state().reset();
    },

    resetToggleState (): void {
        _state = new ToggleState();
    },
};
